import type { ReactNode } from 'react';
import { useCallback, useState } from 'react';
import { vegaCopyConversation } from '@gonfalon/dogfood-flags/dogfood-flags';
import { msg } from '@gonfalon/messages/msg';

import { CopyButton } from '../../internal/components/AgentInsights/components/CopyButton';

import { SpanDetailsIconButton } from './internal/SpanDetailsIconButton';
import { InvestigationRow } from './InvestigationRow';
import { formatPayload } from './JsonPayload';
import type { MessageMetaItem } from './MessageMeta';

import { investigationExtrasHoverReveal } from '../../internal/components/conversation/ConversationChrome.css';
import {
  clamped,
  clampFade,
  clampToggle,
  code,
  errorChip,
  ioActions,
  ioEmpty,
  ioHeader,
  ioLabel,
  statusFail,
  status as statusPill,
  toolBody,
} from './ToolCallRow.css';

export const ToolCallRow: React.FC<{
  icon: ReactNode;

  label: string;

  detail?: string;

  input?: string;

  output?: string;
  renderOutput?: () => ReactNode;

  status?: ReactNode;

  action?: ReactNode;
  onShowDetails?: () => void;
  isError?: boolean;
  isLoading?: boolean;

  defaultExpanded?: boolean;

  clampOutput?: boolean;

  meta?: MessageMetaItem[];
  metaTitle?: string;
  onCopy?: () => void;
}> = ({
  icon,
  label,
  detail,
  input,
  output,
  renderOutput,
  status,
  action,
  onShowDetails,
  isError = false,
  isLoading = false,
  defaultExpanded = false,
  clampOutput = true,
  meta,
  metaTitle,
  onCopy,
}) => {
  const copyEnabled = vegaCopyConversation();
  const [open, setOpen] = useState(defaultExpanded || isError);
  const [seenDefaultExpanded, setSeenDefaultExpanded] = useState(defaultExpanded);
  if (defaultExpanded !== seenDefaultExpanded) {
    setSeenDefaultExpanded(defaultExpanded);
    if (defaultExpanded) {
      setOpen(true);
    }
  }
  const [showAll, setShowAll] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const canExpand = clampOutput && overflows;

  const measureOutput = useCallback((node: HTMLDivElement | null) => {
    if (!node) {
      return undefined;
    }
    const measure = () => {
      if (node.classList.contains(clamped)) {
        setOverflows(node.scrollHeight > node.clientHeight + 1);
      }
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    Array.from(node.children).forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, []);

  const body = renderOutput ? (
    renderOutput()
  ) : output?.trim() ? (
    <pre className={code}>{formatPayload(output)}</pre>
  ) : null;
  const shownInput = input?.trim() ? input : undefined;

  return (
    <InvestigationRow
      flat
      icon={<span aria-hidden>{icon}</span>}
      label={label}
      detail={detail}
      isError={isError}
      isLoading={isLoading}
      expanded={open}
      onToggle={() => {
        setOpen((value) => !value);
      }}
      status={status}
      headerExtra={
        onShowDetails ? (
          <span className={investigationExtrasHoverReveal}>
            <SpanDetailsIconButton onPress={onShowDetails} />
          </span>
        ) : undefined
      }
      meta={meta}
      metaTitle={metaTitle}
    >
      <div className={toolBody}>
        {shownInput || action ? (
          <div className={ioHeader}>
            {shownInput ? <div className={ioLabel}>{msg('observability.conversationTranscript.input')}</div> : null}
            <div className={ioActions}>
              {action}
              {copyEnabled && shownInput ? (
                <CopyButton
                  redesign
                  onCopy={onCopy}
                  text={shownInput}
                  label={msg('vega.conversation.copyInput')}
                  successTitle={msg('vega.conversation.copyInputSuccess')}
                />
              ) : null}
            </div>
          </div>
        ) : null}
        {shownInput ? <pre className={code}>{formatPayload(shownInput)}</pre> : null}
        {body ? (
          <>
            <div className={ioHeader}>
              <div className={ioLabel}>{msg('observability.conversationTranscript.output')}</div>
              {copyEnabled && output?.trim() ? (
                <div className={ioActions}>
                  <CopyButton
                    redesign
                    onCopy={onCopy}
                    text={output}
                    label={msg('vega.conversation.copyOutput')}
                    successTitle={msg('vega.conversation.copyOutputSuccess')}
                  />
                </div>
              ) : null}
            </div>
            <div ref={measureOutput} className={showAll || !clampOutput ? undefined : clamped}>
              {body}
              {showAll || !canExpand ? null : <div className={clampFade} />}
            </div>
            {canExpand || showAll ? (
              <button
                type="button"
                className={clampToggle}
                onClick={() => {
                  setShowAll((value) => !value);
                }}
              >
                {showAll ? msg('vega.conversation.showLess') : msg('vega.conversation.showMore')}
              </button>
            ) : null}
          </>
        ) : null}
        {!shownInput && !body ? (
          <div className={ioEmpty}>{msg('observability.conversationTranscript.noToolIo')}</div>
        ) : null}
      </div>
    </InvestigationRow>
  );
};

export const ErrorChip: React.FC<{ count: number; terse?: boolean }> = ({ count, terse = false }) =>
  count > 0 ? (
    <span className={[statusPill, statusFail, errorChip].join(' ')}>
      {terse && count === 1
        ? msg('observability.conversationTranscript.errorBadge')
        : msg('observability.conversationTranscript.errorCount', { count })}
    </span>
  ) : null;

export const ToolCallStatus: React.FC<{ isError?: boolean }> = ({ isError = false }) =>
  isError ? (
    <span className={[statusPill, statusFail, errorChip].join(' ')}>
      {msg('observability.conversationTranscript.errorBadge')}
    </span>
  ) : null;
